import React, { useState, useEffect, useCallback } from "react";
import Modal from "../../modal/Modal"
import "./WeeklyCalendar.css";

const WeeklyCalendar = ({ apiUrl, bookingApi, successRedirectSection, navigateToSection }) => {
  // Helper: Format date as "YYYY-MM-DD"
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper: Get localized day name (e.g., "Sun", "Mon")
const getDayName = (date) => {
  return new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date);
};

// Helper: Calculate the start of the week (Sunday)
const getStartOfWeek = (date) => {
  const day = date.getDay();
  const diff = date.getDate() - day;
  const start = new Date(date);
  start.setDate(diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

// Helper: Generate weekly dates (Sun to Sat)
const getWeeklyDates = (startOfWeek) => {
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    return {
      day: getDayName(date),
      date: formatDate(date),
    };
  });
};

// Transform data to rows for rendering
const transformDataToRows = (tutors, weeklyDates) => {
  const rows = [];
  tutors.forEach((tutor) => {
    const tutorRows = [];

    // Group slots by date
    const slotsByDate = weeklyDates.map((day) => ({
      date: day.date,
      slots: tutor.slots
        .filter((slot) => slot.date === day.date)
        .sort((a, b) => new Date(`1970-01-01T${a.time}`) - new Date(`1970-01-01T${b.time}`)),
    }));

    // Determine the maximum number of rows needed for this tutor
    const maxRows = Math.max(...slotsByDate.map((day) => day.slots.length));

    // Generate rows for the tutor
    for (let i = 0; i < maxRows; i++) {
      tutorRows.push({
        tutorName: tutor.name, // Show tutor name in every row
        columns: slotsByDate.map((day) => (day.slots[i] ? day.slots[i] : null)),
      });
    }

    rows.push(...tutorRows);
  });

  return rows;
};


  const today = getStartOfWeek(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(today);
  const [weekData, setWeekData] = useState([]);
  const [slotsAvailable, setSlotsAvailable] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  const weeklyDates = getWeeklyDates(currentWeekStart);

  // Fetch tutor slots data from API
  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}?start=${formatDate(currentWeekStart)}`);
      const data = await response.json();
      setWeekData(data.tutors || []);
      setSlotsAvailable(data.tutors?.some((tutor) => tutor.slots.length > 0));
    } catch (error) {
      console.error("Error fetching weekly data:", error);
      setWeekData([]);
      setSlotsAvailable(false);
    }
  }, [apiUrl, currentWeekStart]);

  // Navigate to previous or next week
  const navigateWeek = (direction) => {
    const offset = direction === "prev" ? -7 : 7;
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + offset);
    setCurrentWeekStart(getStartOfWeek(newWeekStart));
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Book Button Click
  const handleBook = (slot) => {
    setSelectedSlot(slot);
    setIsModalOpen(true);
  };

  // Confirm Booking
  const handleConfirmBooking = async () => {
    if (!selectedSlot) return;
    const { tutorName, date, time } = selectedSlot;
    console.log(selectedSlot);

    try {
      const response = await fetch(bookingApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tutorName, date, time }),
      });

      if (!response.ok) throw new Error("Booking failed");

      const result = await response.json();
      setMessage(result.message || "Booking successful!");
      setMessageType("success");
      setIsModalOpen(false);

      // Redirect on success
      if (successRedirectSection && navigateToSection) {
        navigateToSection(successRedirectSection);
      }
    } catch (error) {
      setMessage(error.message || "Booking failed. Please try again.");
      setMessageType("error");
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleCloseMessage = () => {
    setMessage("");
    setMessageType("success");
  };

  if (!slotsAvailable) {
    return <div className="calendar-empty">No slots are available for this week.</div>;
  }

  const transformedRows = transformDataToRows(weekData, weeklyDates);

  return (
    <div className="weekly-calendar">
      <div className="calendar-header">
        <button
          onClick={() => navigateWeek("prev")}
          disabled={currentWeekStart <= today}
        >
          Previous Week
        </button>
        <h3>
          Week: {formatDate(currentWeekStart)} -{" "}
          {formatDate(new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000))}
        </h3>
        <button onClick={() => navigateWeek("next")}>Next Week</button>
      </div>

      <div className="calendar-table">
        {/* Header Row: Dates */}
        <div className="calendar-row calendar-header-row">
          <div className="calendar-cell calendar-tutor-cell">Tutor</div>
          {weeklyDates.map((day, index) => (
            <div key={index} className="calendar-cell calendar-date-cell">
              <div>{day.day}</div>
              <div>{day.date}</div>
            </div>
          ))}
        </div>

        {/* Render Tutor Rows */}
        {transformedRows.map((row, rowIndex) => (
          <div key={rowIndex} className="calendar-row">
            <div className="calendar-cell calendar-tutor-cell">{row.tutorName}</div>
            {row.columns.map((slot, colIndex) => (
              <div key={colIndex} className="calendar-cell">
                {slot ? (
                  <div className="slot-details">
                    <div className="slot-time">
                      <i className="fa fa-clock-o"></i> {slot.time}
                    </div>
                    <div className="slot-duration">
                      <i className="fa fa-hourglass"></i> {slot.duration || "N/A"}
                    </div>
                    <div className="slot-seats">
                      <i className="fa fa-user"></i> Seats Booked: {slot.total - slot.open}
                    </div>
                    <div className="slot-availability">
                      <i className="fa fa-user-o"></i> Seats Open: {slot.open}
                    </div>
                    {slot.open > 0 ? (
                      <button className="book-button" onClick={() => handleBook({ ...slot, tutorName: row.tutorName })}>
                        Book
                      </button>
                    ) : (
                      <button className="book-button" disabled>
                        Fully Booked
                      </button>
                    )}
                  </div>
                ) : (
                  ""
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        message={`Confirm booking for ${selectedSlot?.tutorName} on ${selectedSlot?.date} at ${selectedSlot?.time}?`}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmBooking}
        type="confirm"
      />

      {/* Success/Error Message */}
      {message && (
        <Modal
          isOpen={!!message}
          message={message}
          onClose={handleCloseMessage}
          type={messageType}
        />
      )}
    </div>
  );
};

export default WeeklyCalendar;
